import { FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPurchasesApi } from "../../http/carPurchases/api";
import CarPurchaseDocuments from "../carPurchaseDocuments/CarPurchaseDocuments";
import Header from "../ui/Header";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import CarPurchaseForm, { CarPurchaseFormBody } from "./CarPurchaseForm";

const EditCarPurchase: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.carOrders.edit} />
      <TabsProvider>
        <TabbedEditCarPurchase />
      </TabsProvider>
    </>
  );
};

export default EditCarPurchase;

const TabbedEditCarPurchase: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const carPurchasesApi = useCarPurchasesApi();
  const dispatch = useContext(NotificationDispatch);
  const { id } = useParams<{ id: string }>();
  const aborter = useRef(new AbortController());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [urlParams] = useSearchParams();
  const navigate = useNavigate();

  const tabs = [{ title: i18n.t.common.edit }, { title: i18n.t.common.documents }];

  const carPurchaseSwr = carPurchasesApi.getAdminCarPurchase(id!, aborter.current.signal);
  const [carPurchase, isInitialized] = useDataInitializer(carPurchaseSwr.data, carPurchaseSwr.error);

  useEffect(() => {
    id && carPurchaseSwr.trigger();
  }, [id]);

  useEffect(() => {
    carPurchaseSwr.error && dispatch(showErrorNotification(i18n, carPurchaseSwr.error));
  }, [carPurchaseSwr.error]);

  const onSubmit = async (dataForm: CarPurchaseFormBody) => {
    if (carPurchase?.id) {
      setIsSubmitting(true);
      try {
        await carPurchasesApi.updateAdminCarPurchase(carPurchase.id, {
          subsidiaryId: dataForm.subsidiary.id,
          supplierId: dataForm.supplier?.id,
          leasingCompanyId: dataForm.leasingCompany?.id,
          driverId: dataForm.driver?.id,
          manufacturerId: dataForm.manufacturer.id,
          model: dataForm.model,
          plannedArrivalDate: dataForm.plannedArrivalDate,
          purchaseDate: dataForm.purchaseDate,
          costNet: dataForm.costNet,
          costGross: dataForm.costGross,
          currency: dataForm.currency?.value,
          state: dataForm.state.value,
          optionals: dataForm.optionals,
          purchaseType: dataForm.purchaseType.value,
          currentVersionNumber: carPurchase.versionNumber,
        });

        const redirectUrl = urlParams.get("redirect_url");
        if (redirectUrl) {
          navigate(redirectUrl);
          return;
        }
        if (!dataForm.assign) {
          dispatch(showSuccessNotification(i18n.t));
        }
        carPurchaseSwr.trigger();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const tabComponents: TabContent[] = [
    {
      body: (
        <CarPurchaseForm
          onSubmit={onSubmit}
          carPurchase={carPurchase}
          isDisabled={!carPurchase?.id || !userSettings.permissions.carPurchases.update || isSubmitting}
          isLoading={!isInitialized}
        />
      ),
      eager: true,
      withBorder: true,
    },
    { body: <CarPurchaseDocuments setIsDragging={setIsDragging} />, withBorder: true },
  ];

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} isDragging={isDragging} />
    </>
  );
};
